import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Modal from '../../../components/common/modal';
import { ClientSingleSelectWithSearch } from '../../../components/content-library/filter-controls/ClientSingleSelectWithSearch';
import { BulkCloneGroupsRequest, BulkCloneGroupsRow, useClient } from '../../../hooks/useClients';

interface BulkCloneGroupsModalProps {
  onClose: () => void;
  onConfirm: (payload: BulkCloneGroupsRequest) => Promise<unknown>;
  isSubmitting?: boolean;
}

const HEADER_ALIASES: Record<string, keyof BulkCloneGroupsRow> = {
  'group name': 'name',
  name: 'name',
  'group code': 'client_code',
  client_code: 'client_code',
  'contract date': 'contract_date',
  contract_date: 'contract_date',
  'go live date': 'go_live_date',
  go_live_date: 'go_live_date',
  'plan code': 'package_code',
  package_code: 'package_code',
  plan_code: 'package_code',
};

const normalizeHeader = (header: string): keyof BulkCloneGroupsRow | null => {
  const key = header.trim().toLowerCase().replace(/_/g, ' ');
  return HEADER_ALIASES[key] ?? HEADER_ALIASES[header.trim().toLowerCase()] ?? null;
};

type ParsedCsvRow = {
  fields: string[];
  lineNumber: number;
};

const MAX_BULK_CLONE_ROWS = 100;

/**
 * Split CSV into rows while respecting quoted commas and newlines.
 * `unclosedQuoteLine` is the line where a quote opened and never closed.
 */
const parseCsvRows = (text: string): { rows: ParsedCsvRow[]; unclosedQuoteLine: number | null } => {
  const normalized = text
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  const rows: ParsedCsvRow[] = [];
  let fields: string[] = [];
  let current = '';
  let inQuotes = false;
  let quoteStartLine = 1;
  let lineNumber = 1;
  let rowStartLine = 1;

  const pushRow = () => {
    fields.push(current.trim());
    rows.push({ fields, lineNumber: rowStartLine });
    fields = [];
    current = '';
  };

  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i];
    if (char === '"') {
      if (inQuotes && normalized[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
        if (inQuotes) {
          quoteStartLine = lineNumber;
        }
      }
      continue;
    }
    if (char === ',' && !inQuotes) {
      fields.push(current.trim());
      current = '';
      continue;
    }
    if (char === '\n') {
      lineNumber += 1;
      if (inQuotes) {
        current += char;
      } else {
        pushRow();
        rowStartLine = lineNumber;
      }
      continue;
    }
    current += char;
  }

  if (inQuotes) {
    return { rows: [], unclosedQuoteLine: quoteStartLine };
  }

  if (current.length > 0 || fields.length > 0) {
    pushRow();
  }

  return { rows, unclosedQuoteLine: null };
};

const isEmptyCsvRow = (fields: string[]): boolean => fields.every((field) => !field.trim());

const parseDateToIso = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const iso = moment(
    trimmed,
    ['YYYY-MM-DD', 'M/D/YYYY', 'MM/DD/YYYY', 'M/D/YY', 'MM/DD/YY', 'M-D-YYYY', 'MM-DD-YYYY'],
    true,
  );
  if (!iso.isValid()) {
    return null;
  }
  return iso.format('YYYY-MM-DD');
};

export const parseBulkCloneCsv = (text: string): { rows: BulkCloneGroupsRow[]; error: string | null } => {
  const { rows: parsedCsvRows, unclosedQuoteLine } = parseCsvRows(text);
  if (unclosedQuoteLine !== null) {
    return { rows: [], error: `Row ${unclosedQuoteLine}: unmatched quote (").` };
  }
  const csvRows = parsedCsvRows.filter((row) => !isEmptyCsvRow(row.fields));

  if (csvRows.length < 2) {
    return { rows: [], error: 'CSV must include a header row and at least one data row.' };
  }
  // Matches the backend's BulkCloneGroupsRequestSchema limit.
  if (csvRows.length - 1 > MAX_BULK_CLONE_ROWS) {
    return {
      rows: [],
      error: `CSV has ${csvRows.length - 1} groups; the maximum per upload is ${MAX_BULK_CLONE_ROWS}.`,
    };
  }

  const headerFields = csvRows[0].fields;
  const columnMap: (keyof BulkCloneGroupsRow | null)[] = headerFields.map(normalizeHeader);

  const required: (keyof BulkCloneGroupsRow)[] = [
    'name',
    'client_code',
    'contract_date',
    'go_live_date',
    'package_code',
  ];
  for (const field of required) {
    if (!columnMap.includes(field)) {
      return {
        rows: [],
        error: `Missing required column: ${field.replace(/_/g, ' ')} (e.g. Group Name, Group Code, Contract Date, Go Live Date, Plan Code).`,
      };
    }
  }

  const rows: BulkCloneGroupsRow[] = [];

  for (let i = 1; i < csvRows.length; i++) {
    const { fields: values, lineNumber } = csvRows[i];
    const record: Partial<BulkCloneGroupsRow> = {};

    columnMap.forEach((field, colIndex) => {
      if (!field) {
        return;
      }
      record[field] = values[colIndex]?.trim() ?? '';
    });

    const contractIso = parseDateToIso(record.contract_date ?? '');
    const goLiveIso = parseDateToIso(record.go_live_date ?? '');

    if (!record.name?.trim() || !record.client_code?.trim() || !record.package_code?.trim()) {
      return { rows: [], error: `Row ${lineNumber}: name, group code, and plan code are required.` };
    }
    if (!contractIso || !goLiveIso) {
      return {
        rows: [],
        error: `Row ${lineNumber}: invalid date format (use YYYY-MM-DD, M/D/YYYY or M/D/YY).`,
      };
    }

    rows.push({
      name: record.name.trim(),
      client_code: record.client_code.trim(),
      contract_date: contractIso,
      go_live_date: goLiveIso,
      package_code: record.package_code.trim(),
    });
  }

  return { rows, error: null };
};

const BulkCloneGroupsModal: React.FC<BulkCloneGroupsModalProps> = ({ onClose, onConfirm, isSubmitting = false }) => {
  const [sourceClientId, setSourceClientId] = useState<number | null>(null);
  const [sourcePackageId, setSourcePackageId] = useState<number | null>(null);
  const [parsedRows, setParsedRows] = useState<BulkCloneGroupsRow[]>([]);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: sourceClient } = useClient(sourceClientId ?? undefined);

  const activePackages = useMemo(() => {
    return (sourceClient?.packages ?? []).filter((pkg) => !pkg.archived_at);
  }, [sourceClient?.packages]);

  const selectedPackage = activePackages.find((pkg) => pkg.id === sourcePackageId);

  const handleSourceChange = (clientId: number | null) => {
    setSourceClientId(clientId);
    setSourcePackageId(null);
    setShowConfirmation(false);
    setErrorMessage(null);
  };

  // Pre-select the source's default plan, but never replace a plan that's already chosen
  // (useClient refetches on window focus, which gives activePackages a new reference).
  useEffect(() => {
    if (!sourceClientId || sourcePackageId || activePackages.length === 0) {
      return;
    }
    const defaultId = sourceClient?.eligibility_config?.default_package;
    if (defaultId && activePackages.some((pkg) => pkg.id === defaultId)) {
      setSourcePackageId(defaultId);
      return;
    }
    if (activePackages.length === 1 && activePackages[0].id) {
      setSourcePackageId(activePackages[0].id);
    }
  }, [sourceClientId, sourcePackageId, sourceClient?.eligibility_config?.default_package, activePackages]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setParseError(null);
    setErrorMessage(null);
    setParsedRows([]);
    setCsvFileName(file.name);
    setShowConfirmation(false);

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const { rows, error } = parseBulkCloneCsv(text);
      if (error) {
        setParseError(error);
        setParsedRows([]);
        return;
      }
      setParsedRows(rows);
    };
    reader.onerror = () => {
      setParseError('Failed to read the CSV file.');
    };
    reader.readAsText(file);
  };

  const canContinue = !!sourceClientId && !!sourcePackageId && parsedRows.length > 0 && !parseError && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canContinue || !sourceClientId || !sourcePackageId) {
      return;
    }

    if (!showConfirmation) {
      setErrorMessage(null);
      setShowConfirmation(true);
      return;
    }

    try {
      await onConfirm({
        source_client_id: sourceClientId,
        source_package_id: sourcePackageId,
        rows: parsedRows,
      });
      handleClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to bulk clone groups';
      setErrorMessage(message);
    }
  };

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div className="inline-block w-full transform overflow-visible rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-lg sm:align-middle">
          <form onSubmit={handleSubmit}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload Groups</h3>
              <button
                type="button"
                className="text-blue-primary text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Close
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              {!showConfirmation ? (
                <>
                  <p className="text-sm text-gray-600">
                    Clone new groups from a source group and plan. Upload a CSV with columns: Group Name, Group Code,
                    Contract Date, Go Live Date, and Plan Code.
                  </p>
                  <ClientSingleSelectWithSearch
                    label="Source Group"
                    selectedClientId={sourceClientId}
                    onChange={handleSourceChange}
                    placeholder="Select a source group"
                    showClearOption={false}
                    disabled={isSubmitting}
                  />
                  {sourceClientId && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Source Plan</label>
                      <select
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"
                        value={sourcePackageId ?? ''}
                        onChange={(e) => setSourcePackageId(e.target.value ? Number(e.target.value) : null)}
                        disabled={isSubmitting || activePackages.length === 0}
                        data-testid="bulk-clone-source-package"
                      >
                        <option value="">Select a plan</option>
                        {activePackages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} ({pkg.code})
                          </option>
                        ))}
                      </select>
                      {activePackages.length === 0 && (
                        <p className="mt-1 text-sm text-red-600">This group has no active plans.</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Groups CSV</label>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleFileChange}
                      disabled={isSubmitting}
                      className="block w-full text-sm text-gray-600"
                      data-testid="bulk-clone-csv-input"
                    />
                    {csvFileName && !parseError && parsedRows.length > 0 && (
                      <p className="mt-2 text-sm text-gray-600">
                        <strong>{csvFileName}</strong>: {parsedRows.length} group
                        {parsedRows.length === 1 ? '' : 's'} ready to create.
                      </p>
                    )}
                    {parseError && (
                      <p className="mt-2 text-sm text-red-600" data-testid="bulk-clone-parse-error">
                        {parseError}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-900">Confirm bulk group creation</p>
                  <p className="text-sm text-gray-600">
                    Create <strong>{parsedRows.length}</strong> group{parsedRows.length === 1 ? '' : 's'} cloned from{' '}
                    <strong>{sourceClient?.name ?? `Group #${sourceClientId}`}</strong> using the{' '}
                    <strong>
                      {selectedPackage ? `${selectedPackage.name} (${selectedPackage.code})` : 'selected'}
                    </strong>{' '}
                    plan?
                  </p>
                </div>
              )}
              {errorMessage && (
                <p className="text-sm text-red-600" data-testid="bulk-clone-error">
                  {errorMessage}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              {showConfirmation && (
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
                  onClick={() => {
                    setShowConfirmation(false);
                    setErrorMessage(null);
                  }}
                  disabled={isSubmitting}
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!canContinue}
                data-testid="bulk-clone-submit-button"
              >
                {isSubmitting ? 'Creating…' : showConfirmation ? 'Confirm Upload' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      }
    />
  );
};

export default BulkCloneGroupsModal;
